// src/components/DocumentLogGrid.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { Table, Typography } from 'antd';
import { getdocumentlog } from '../../../services/documentLogService';

const { Text } = Typography;

export const DocumentLog = ({ objectId, userId }) => {

  const [data, setdata] = useState([]);
  const [loading, setloading] = useState(false);
  const [total, settotal] = useState(0);
  const [tableparams, settableparams] = useState({
    pagination: {
      current: 1,
      pageSize: 50,
      showSizeChanger: true,
      pageSizeOptions: ['25', '50', '100'],
    },
  });

  // track sort and filter state for the table
  const [sortedinfo, setsortedinfo] = useState({});
  const [filteredinfo, setfilteredinfo] = useState({});

  // build filter lists from current data for user / event / version
  const userfilters = useMemo(() => {
    const values = Array.from(
      new Set((data || []).map(row => row.userId).filter(Boolean))
    );
    return values.map(v => ({ text: v, value: v }));
  }, [data]);

  const eventfilters = useMemo(() => {
    const values = Array.from(
      new Set((data || []).map(row => row.eventName).filter(Boolean))
    );
    return values.map(v => ({ text: v, value: v }));
  }, [data]);

  const versionfilters = useMemo(() => {
    const values = Array.from(
      new Set((data || []).map(row => row.version).filter(Boolean))
    );
    return values.map(v => ({ text: v, value: v }));
  }, [data]);

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 200,
      render: (value, record) => {
        const extension = record.fileName
          ? record.fileName.split('.').pop()
          : '';
        return (
          <div
            style={{
              overflow: 'hidden',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              className={`file-icon-small files-small-${extension}`}
            ></div>
            <div
              style={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                width: '100%',
                padding: '0 10px',
              }}
              title={value}
            >
              {value}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Event Date',
      dataIndex: 'logDate',
      key: 'logDate',
      width: 190,
      sorter: (a, b) => {
        const ad = a.logDate ? new Date(a.logDate) : new Date(0);
        const bd = b.logDate ? new Date(b.logDate) : new Date(0);
        return ad.getTime() - bd.getTime();
      },
      sortOrder: sortedinfo.columnKey === 'logDate' && sortedinfo.order,
      render: value => {
        const date = value ? new Date(value) : null;
        return date ? date.toLocaleString() : '';
      },
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 100,
      filters: userfilters,
      filteredValue: filteredinfo.userId || null,
      onFilter: (value, record) => record.userId === value,
      sorter: (a, b) => {
        const av = a.userId || '';
        const bv = b.userId || '';
        return av.localeCompare(bv);
      },
      sortOrder: sortedinfo.columnKey === 'userId' && sortedinfo.order,
    },
    {
      title: 'Event',
      dataIndex: 'eventName',
      key: 'eventName',
      width: 200,
      filters: eventfilters,
      filteredValue: filteredinfo.eventName || null,
      onFilter: (value, record) => record.eventName === value,
      sorter: (a, b) => {
        const av = a.eventName || '';
        const bv = b.eventName || '';
        return av.localeCompare(bv);
      },
      sortOrder: sortedinfo.columnKey === 'eventName' && sortedinfo.order,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 75,
      filters: versionfilters,
      filteredValue: filteredinfo.version || null,
      onFilter: (value, record) => record.version === value,
      sorter: (a, b) => {
        const av = a.version || '';
        const bv = b.version || '';
        return av.localeCompare(bv, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      },
      sortOrder: sortedinfo.columnKey === 'version' && sortedinfo.order,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      width: 100,
      sorter: (a, b) => {
        const av = a.message || '';
        const bv = b.message || '';
        return av.localeCompare(bv);
      },
      sortOrder: sortedinfo.columnKey === 'message' && sortedinfo.order,
      render: value => (
        <Text ellipsis title={value}>
          {value}
        </Text>
      ),
    },
  ];

  const fetchdata = async () => {
    setloading(true);

    const req = tableparams.request || {
      objectId,
      limit: 50,
      skip: 0,
      order: [],
      userSqlFilter: {}
    };

    try {
      const result = await getdocumentlog(req);

      setdata(result.records || []);
      settotal(result.recordCount || 0);

      settableparams(prev => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          total: result.recordCount || 0
        }
      }));
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    console.log('Table params changed:', tableparams);
  }, [tableparams]);

  useEffect(() => {
    console.log('FETCH DATA')
    // load on mount and when object/user or paging changes
    fetchdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    objectId,
    tableparams.pagination.current,
    tableparams.pagination.pageSize,
    tableparams.sorter,
  ]);

  const handletablechange = (pagination, filters, sorter) => {
    console.log('Table change:', { pagination, filters, sorter });
    // calculate skip & limit
    const limit = pagination.pageSize;
    const skip = (pagination.current - 1) * pagination.pageSize;

    // get order structure expected by api
    let order = [];
    if (sorter && sorter.columnKey) {
      order.push({
        column: sorter.columnKey,
        direction: sorter.order === 'descend' ? 1 : 0
      });
    }

    // collect filters into a flat object
    const userSqlFilter = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key].length > 0) {
        userSqlFilter[key] = filters[key][0]; // take first value
      }
    });

    settableparams(prev => ({
      ...prev,
      sorter: sorter,
      filterer: filters,
      pagination,
      request: {
        objectId,
        limit,
        skip,
        order,
        userSqlFilter
      }
    }));
  };

  return (
    <Table
      className="full-height-table"
      rowKey={(record, index) =>
        `${record.fileName}-${record.logDate}-${index}`
      }
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={tableparams.pagination}
      onChange={handletablechange}
      scroll={{ y: '100%', x: 'max-content' }}
      size="small"
    />
  );
};

export default DocumentLog;
